import React, { useState, useEffect, useRef } from "react";
import { FormGroup, Label, Input } from "reactstrap";
import { getAllRoomsWithoutPagination } from "../../helpers/api.ts";

const RoomDropdown = ({
  onRoomSelect,
  initialRoomId,
  className = "",
  disabled = false,
}) => {
  const [rooms, setRooms] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [tarrif, setTariff] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    fetchRooms();
    // Add click outside listener
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Effect to handle initialRoomId
  useEffect(() => {
    if (initialRoomId && rooms.length > 0) {
      const room = rooms.find(r => r.room_id === initialRoomId);
      if (room) {
        handleSelectRoom(room);
      }
    }
  }, [initialRoomId, rooms]);

  const fetchRooms = async () => {
    setLoading(true);
    setError("");

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      let hotel_id1;
      if(user?.role === "RECEPTIONIST"){
        hotel_id1 = user?.hotel?.hotel_id;
      } else{
        hotel_id1 = user?.hotel_id;
      }

      // if (!hotel_id1) {
      //   throw new Error("Hotel ID not found");
      // }

      const userData = {
        hotel_id: hotel_id1,
      };

      const response = await getAllRoomsWithoutPagination(userData);

      if (response?.success) {
        // Include all rooms in edit mode, otherwise only show available rooms
        const roomsToShow = initialRoomId 
          ? response.data 
          : response.data?.filter(room => room.roomstatus === "AVAILABEL");
        
        setRooms(roomsToShow || []);

        if (!roomsToShow || roomsToShow.length === 0) {
          setError(initialRoomId ? "No rooms found" : "No available rooms found");
        }

        // If we have an initialRoomId, select that room
        if (initialRoomId) {
          const initialRoom = response.data?.find(r => r.room_id === initialRoomId);
          if (initialRoom) {
            handleSelectRoom(initialRoom);
          }
        }
      } else {
        throw new Error(response?.message || "Failed to fetch rooms");
      }
    } catch (error) {
      console.error("Error fetching rooms:", error);
      setError(error.message || "Error fetching rooms");
    } finally {
      setLoading(false);
    }
  };

  // Filter rooms based on search query
  const filteredRooms = rooms?.filter((room) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (room.room_number?.toLowerCase() || '').includes(searchLower) ||
      (room.room_type?.toLowerCase() || '').includes(searchLower) ||
      (room.ac_no_ac?.toLowerCase() || '').includes(searchLower) ||
      (room.room_name?.toLowerCase() || '').includes(searchLower)
    );
  });
  

  const handleSelectRoom = (room) => {
    setSelectedRoom(room);
    setSearchQuery(`${room.room_number} - ${room.room_type} (${room.ac_no_ac}) - ₹${room.tariff}`);
    setShowSuggestions(false);
    onRoomSelect(room);
  };

  return (
    <FormGroup className={className}>
      <Label>Select Room</Label>
      <div className="room-selection-container position-relative" ref={wrapperRef}>
        <Input
          type="text"
          placeholder={loading ? "Loading rooms..." : "Search room by number, type or AC/Non-AC..."}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowSuggestions(true);
            if (!e.target.value) {
              setSelectedRoom(null);
            }
          }}
          onFocus={() => setShowSuggestions(true)}
          disabled={disabled || loading}
        />
        {showSuggestions && filteredRooms && filteredRooms.length > 0 && (
          <div 
            className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" 
            style={{ 
              zIndex: 1000, 
              maxHeight: '200px', 
              overflowY: 'auto'
            }}
          >
            {filteredRooms.map((room) => (
              <div
                key={room.room_id}
                className="p-2 cursor-pointer hover-bg-light"
                style={{ 
                  cursor: 'pointer',
                  borderBottom: '1px solid #eee',
                  backgroundColor: selectedRoom?.room_id === room.room_id ? '#f8f9fa' : 'white'
                }}
                onClick={() => handleSelectRoom(room)}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = selectedRoom?.room_id === room.room_id ? '#f8f9fa' : 'white'}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Room {room.room_number}</strong> - {room.room_type}
                  </div>
                  <div>
                    <strong>{room.room_name}</strong>
                  </div>
                  <div>
                    <span className="text-muted">{room.ac_no_ac}</span>
                    <span className="ms-2 text-success">₹{room.tariff}</span>
                    {room.roomstatus !== "AVAILABEL" && (
                      <span className="ms-2 badge bg-warning">{room.roomstatus}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {error && <div className="text-danger mt-1">{error}</div>}
    </FormGroup>
  );
};

export default RoomDropdown;
